import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  DropdownItem,
  Header,
  Label,
  Table,
} from 'semantic-ui-react';
import { Icon } from 'semantic';
import { ChargingPolicy } from '../types';
import Flex from 'components/Layout/helpers/Flex';
import { useHistory } from 'react-router-dom';
import { Group } from 'components/Layout/helpers';

export default function ChargingPolicies() {
  const mockChargingPolicies: ChargingPolicy[] = [
    {
      name: 'Alize-FR',
      label: 'Alize-FR',
      description: 'For AC',
      status: 'Active',
      currency: 'EUR',
      pricing:
        'Cost of €1 connection and €0.30/kWh. Price of €1/h for the first 2 hours (8-20) then €4/h after 2 hours (8-20) and €0.30/h (20-8)',
      associatedGroups: ['Paris-1', 'Paris-2'],
      sessionThresholds: {
        minTimeMinutes: undefined,
        maxTimeMinutes: undefined,
        minKwhConsumed: undefined,
        maxKwhConsumed: undefined,
        maxCost: undefined,
      },
    },
    {
      label: 'policy-2',
      name: 'Policy ABC',
      description: 'Base tariffs for all+discount for Disney/FR',
      status: 'Active',
      currency: 'EUR',
      pricing: '€0.27/kWh, €0.47/session, €0.15/hour',
      associatedGroups: ['Paris-1', 'Paris-2'],
      sessionThresholds: {
        minTimeMinutes: undefined,
        maxTimeMinutes: undefined,
        minKwhConsumed: undefined,
        maxKwhConsumed: undefined,
        maxCost: undefined,
      },
    },
    {
      name: 'Alize-FR-Home',
      description: 'employees Paris',
      status: 'Active',
      currency: 'EUR',
      pricing: '€0.27/kWh',
      associatedGroups: ['Paris-1', 'Paris-2'],
      label: 'policy-3',
      sessionThresholds: {
        minTimeMinutes: undefined,
        maxTimeMinutes: undefined,
        minKwhConsumed: undefined,
        maxKwhConsumed: undefined,
        maxCost: undefined,
      },
    },
    {
      name: 'Alize-FR-Home',
      description: 'employees Paris',
      status: 'Not in use',
      currency: 'EUR',
      pricing: '€0.27/kWh',
      associatedGroups: [],
      label: 'policy-4',
      sessionThresholds: {
        minTimeMinutes: undefined,
        maxTimeMinutes: undefined,
        minKwhConsumed: undefined,
        maxKwhConsumed: undefined,
        maxCost: undefined,
      },
    },
  ];

  return <ChargingPoliciesTable items={mockChargingPolicies} />;
}

function ChargingPoliciesTable({ items }: { items: any[] }) {
  const { t } = useTranslation();
  const historyStack = useHistory();

  return (
    <Fragment>
      <Flex direction="row" justify="space-between" align="center">
        <Header as="h2" style={{ marginTop: '1em' }}>
          {t('chargingPoliciesTable.title', 'Charging Policies')}
        </Header>
        <Button
          onClick={() =>
            historyStack.push(
              '/charging-stations/policies-and-groups/charging-policies/new'
            )
          }>
          New Charging Policy
        </Button>
      </Flex>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t(
                'chargingPoliciesTable.columnNameAndDesc',
                'Name & Description'
              )}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('chargingPoliciesTable.columnStatus', 'Status')}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('chargingPoliciesTable.columnCurrency', 'Currency')}
            </Table.HeaderCell>
            <Table.HeaderCell width={4}>
              {t('chargingPoliciesTable.columnPricing', 'Pricing')}
            </Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('chargingPoliciesTable.columnAssociated', 'Associated')}
            </Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {t('common.actions', 'Actions')}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <strong>{item.name}</strong>
                <br />
                {item.description}
              </Table.Cell>
              <Table.Cell>
                <ChargingPolicyStatusLabel status={item.status} />
              </Table.Cell>
              <Table.Cell>{item.currency}</Table.Cell>
              <Table.Cell>{item.pricing}</Table.Cell>
              <Table.Cell>
                {item.associatedGroups.length > 0
                  ? item.associatedGroups.join(', ')
                  : t(
                      'chargingPoliciesTable.noAssociated',
                      'No associated groups'
                    )}
              </Table.Cell>
              <Table.Cell>
                <Fragment>
                  <Button
                    basic
                    icon="pen-to-square"
                    title={t('common.edit', 'Edit')}
                    onClick={() =>
                      historyStack.push(
                        `/charging-stations/policies-and-groups/charging-policies/${item.label}/edit`
                      )
                    }
                  />

                  <Dropdown
                    text=""
                    icon=""
                    trigger={<Button icon="ellipsis-vertical" basic />}>
                    <Dropdown.Menu>
                      <DropdownItem>
                        <Group>
                          <Icon name="copy" />
                          {t('common.duplicate', 'Duplicate')}
                        </Group>
                      </DropdownItem>
                      <DropdownItem>
                        <Group>
                          <Icon name="trash" />
                          {t('common.archive', 'Archive')}
                        </Group>
                      </DropdownItem>
                    </Dropdown.Menu>
                  </Dropdown>
                </Fragment>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Fragment>
  );
}

function ChargingPolicyStatusLabel({ status }: { status?: string }) {
  const { t } = useTranslation();

  const color = useMemo(() => {
    if (status === 'Active') {
      return 'olive';
    }
    if (status === 'Not in use') {
      return 'grey';
    }
    return 'red';
  }, [status]);

  const content = useMemo(() => {
    if (status === 'Active') {
      return t('chargingPolicyStatus.Active', 'Active');
    }
    if (status === 'Not in use') {
      return t('chargingPolicyStatus.NotInUse', 'Not in use');
    }
    return t('chargingPolicyStatus.Unknown', 'Unknown');
  }, [status]);

  return <Label color={color} content={content} />;
}
